import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { useDispatch } from 'react-redux';
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader, Row } from 'reactstrap';

import FormInput from '../../../components/form/FormInput';
import FormSelect from '../../../components/form/FormSelect';
import FormToggle from '../../../components/form/FormToggle';

import { processAllOptionGet } from '../../../store/slices/tenantSlices/dailyReportSlice';
import {
  dispositionAdd,
  dispositionListGetById,
  dispositionUpdate
} from '../../../store/slices/tenantSlices/settingSlice';
import { fieldTypeOptions } from '../../../utils/common';

const AddEditDispositionModal = ({ isOpen, onCloseClick, isEdit = false, editRecord = null }) => {
  const dispatch = useDispatch();
  const [processOptions, setProcessOptions] = useState([]);

  const [fieldData, setFieldData] = useState([
    {
      edit: 'true',
      label: '',
      type: 'text',
      option: ''
    }
  ]);

  const initialValues = {
    dispocode: '',
    disponame: '',
    active: 'true',
    process_id: '',
    fields: []
  };

  const validationSchema = Yup.object({
    dispocode: Yup.string().required('Disposition Code is required'),
    disponame: Yup.string().required('Disposition Name is required'),
    process_id: Yup.string().required('Process is required')
  });

  const onSubmit = async () => {
    if (isEdit) {
      try {
        const payload = { ...values, id: editRecord.id };
        const fData = [];
        fieldData.forEach((item, index) =>
          fData.push({ ...item, field_rank: `disposition_custom_field_${index + 1}` })
        );
        payload['fields'] = fData;
        const response = await dispatch(dispositionUpdate(payload)).unwrap();
        if (response && response?.message) {
          toast.success('Record Updated Successfully');
          onCloseClick(true);
          resetForm();
          setFieldData([
            {
              edit: 'true',
              label: '',
              type: 'text',
              option: ''
            }
          ]);
        } else {
          toast.error(response.error);
        }
      } catch (error) {
        console.log('error', error);
      }
    } else {
      try {
        const payload = { ...values };
        const fData = [];
        fieldData.forEach((item, index) =>
          fData.push({ ...item, field_rank: `disposition_custom_field_${index + 1}` })
        );
        payload['fields'] = fData;
        const response = await dispatch(dispositionAdd(payload)).unwrap();
        if (response && response?.message) {
          toast.success('Record Added Successfully');
          onCloseClick(true);
          resetForm();
          setFieldData([
            {
              edit: 'true',
              label: '',
              type: 'text',
              option: ''
            }
          ]);
        } else {
          toast.error(response.error);
        }
      } catch (error) {
        console.log('error', error);
      }
    }
  };

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    touched,
    errors,
    resetForm,
    setValues,
    setFieldValue
  } = useFormik({
    initialValues,
    validationSchema,
    onSubmit
  });

  const fetchDispoRecord = async () => {
    try {
      const response = await dispatch(dispositionListGetById(editRecord?.id)).unwrap();
      setValues({
        ...values,
        dispocode: response.DisposCode,
        disponame: response.DisposName,
        process_id: response.ProcessId,
        active: String(response.active)
      });
      setFieldData([
        ...(response?.fields || [
          {
            edit: 'true',
            label: '',
            type: 'text',
            option: ''
          }
        ])
      ]);
    } catch (error) {
      console.log('error', error);
    }
  };

  const getProcessOption = async () => {
    try {
      const response = await dispatch(processAllOptionGet()).unwrap();
      if (response && response.data) {
        const options = response.data.map((val) => ({
          label: val.name,
          value: val.id
        }));
        setProcessOptions(options);
      } else {
        setProcessOptions([]);
      }
    } catch (error) {
      setProcessOptions([]);
      console.log('error', error);
    }
  };

  useEffect(() => {
    getProcessOption();
  }, []);

  useEffect(() => {
    if (isEdit) {
      fetchDispoRecord();
    }
  }, [editRecord]);

  const handleFieldChange = (key, value, index) => {
    const data = [...fieldData];
    data[index][key] = value;
    setFieldData(data);
  };

  return (
    <Modal isOpen={isOpen} size="xl" centered>
      <ModalHeader className="justify-content-center py-2">
        <div className="fs-5">{isEdit ? 'Edit Disposition' : 'Add Disposition'}</div>
      </ModalHeader>
      <ModalBody className="px-4">
        <form id="crms" onSubmit={handleSubmit}>
          <Row>
            <Col lg="3">
              <FormInput
                name="dispocode"
                label="Disposition Code"
                placeholder="Enter Disposition Code"
                value={values.dispocode}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormInput
                name="disponame"
                label="Disposition Name"
                placeholder="Enter Disposition Name"
                value={values.disponame}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
              />
            </Col>
            <Col lg="3">
              <FormToggle
                name="active"
                label="Active"
                value={values.active}
                onChange={(e) => setFieldValue('active', e)}
              />
            </Col>
            <Col lg="3">
              <FormSelect
                name="process_id"
                label="Process"
                placeholder="Select Process"
                value={values.process_id}
                options={processOptions}
                onChange={handleChange}
                onBlur={handleBlur}
                touched={touched}
                errors={errors}
                menuPlacement="bottom"
              />
            </Col>
          </Row>
          {fieldData?.map((val, index, array) => {
            return (
              <Row key={index}>
                <Col lg="3">
                  <FormToggle
                    name="edit"
                    label="Edit"
                    value={val.edit}
                    onChange={(e) => handleFieldChange('edit', e, index)}
                  />
                </Col>
                <Col lg="3">
                  <FormInput
                    name="label"
                    label={`Label ( disposition_custom_field_${index + 1} )`}
                    placeholder="Enter Label"
                    value={val.label}
                    onChange={(e) => handleFieldChange('label', e.target.value, index)}
                  />
                </Col>
                <Col lg="3">
                  <FormSelect
                    name="type"
                    label="Type"
                    placeholder="Select Type"
                    value={val.type}
                    options={fieldTypeOptions}
                    onChange={(e) => handleFieldChange('type', e.target.value, index)}
                  />
                </Col>
                {val.type === 'select' && (
                  <Col lg="3">
                    <FormInput
                      name="option"
                      label="Options"
                      placeholder="Enter Options"
                      value={val.option}
                      onChange={(e) => handleFieldChange('option', e.target.value, index)}
                    />
                  </Col>
                )}
              </Row>
            );
          })}
        </form>
      </ModalBody>
      <ModalFooter>
        <Button
          onClick={() => {
            onCloseClick();
            resetForm();
            setFieldData([
              {
                edit: 'true',
                label: '',
                type: 'text',
                option: ''
              }
            ]);
          }}
          className="px-5 bg-transparent text-primary">
          Cancel
        </Button>
        <Button form="crms" type="submit" className="px-5 bg-primary border-primary">
          {isEdit ? 'Update' : 'Save'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

AddEditDispositionModal.propTypes = {
  isOpen: PropTypes.bool,
  isEdit: PropTypes.bool,
  onCloseClick: PropTypes.func,
  editRecord: PropTypes.any
};

export default AddEditDispositionModal;
